import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { IUserSearchOption } from '@/common/models/User'
import type { ApplicationList, ApplicationListResponse, EngagementCardData, EngagementDetail } from '../../services/types'

interface IClientCardState {
  isOpen: boolean
  loading: boolean
  usersSearchResult: IUserSearchOption[]
  usersSearchResultError: string
  clients: { [id: string]: EngagementCardData }
  editEngagementCardIndex: string | null,
  currentEngagement: EngagementDetail,
  applicationList: ApplicationList,
}
 
const initialState: IClientCardState = {
  isOpen: false,
  loading: false,
  usersSearchResult: [],
  usersSearchResultError: '',
  clients: {},
  editEngagementCardIndex: null,
  currentEngagement: {
    engagementID: '',
    engagementName: '',
    clientID: '',
    clientName: ''
  },
  applicationList: [],
}

function sortClientsBySignOffDueDateHelper(state: IClientCardState) {
  const sortedClients = Object.values(state.clients)
    .sort((a, b) => {
      const dateA = new Date(a.signOffDueDate || 0).getTime();
      const dateB = new Date(b.signOffDueDate || 0).getTime();
      return dateA - dateB; // Ascending order
    });

  // Rebuild the clients object with sorted order
  state.clients = sortedClients.reduce((acc, client) => {
    if (client.id !== null) {
      acc[client.id] = client;
    }
    return acc;
  }, {} as { [id: string]: EngagementCardData });
}

export const clientCardSlice = createSlice({
  name: 'clientCard',
  initialState,
  reducers: {
    openClientCard: (
      state: IClientCardState, 
      action: PayloadAction<string | null>
    ) => {
      const id = action.payload
      if (id !== null) {
        return {...state, isOpen: true, editEngagementCardIndex: id }
      }
      return {...state, isOpen: true, editEngagementCardIndex: null }
    },
    closeClientCard: (state: IClientCardState) => ({ ...state, isOpen: false, editEngagementCardIndex: null }),
    addClient: (
      state: IClientCardState,
      action: PayloadAction<EngagementCardData>,
    ) => {
      const newClient = action.payload
      if (newClient.id !== null) {
        state.clients[newClient.id] = newClient

        // Sort the clients by signOffDueDate
        sortClientsBySignOffDueDateHelper(state)
        state.editEngagementCardIndex = null // Reset the editEngagementCardIndex after adding a new client
      }
    },
    deleteClient: (
      state: IClientCardState, 
      action: PayloadAction<string | null>
    ) => {
      const id = action.payload
      if (id !== null && state.clients[id]) {
        delete state.clients[id]
      }
    },
    updateClient(state: IClientCardState, action: PayloadAction<{ index: number, id: string | null, engagementCode: string, clientId: string, clientName: string, engagementName: string, team: IUserSearchOption[], signOffDueDate: string | null | undefined }>) {
      const index = action.payload.index
      const id = action.payload.id
      const clientId = action.payload.clientId
      const clientName = action.payload.clientName
      const engagementName = action.payload.engagementName
      const engagementCode = action.payload.engagementCode
      const signOffDueDate = action.payload.signOffDueDate
      const engagementUsers= action.payload.team       

      if (state.clients[index]) {
        state.clients[index].id = id
        state.clients[index].engagementCode = engagementCode
        state.clients[index].engagementName = engagementName
        state.clients[index].clientId = clientId
        state.clients[index].clientName = clientName
        state.clients[index].signOffDueDate = signOffDueDate
        state.clients[index].team = engagementUsers

        // Sort the clients by signOffDueDate
        sortClientsBySignOffDueDateHelper(state)
      }
    },
    setLoading: (state: IClientCardState, action: PayloadAction<boolean>) => {
      state.loading = action.payload  
    },
    setCurrentEngagement: (state: IClientCardState, action: PayloadAction<EngagementDetail>) => {
      state.currentEngagement = action.payload
    },
    setApplicationList: (state: IClientCardState, action: PayloadAction<ApplicationListResponse>) => {
      state.applicationList = action.payload.pagedData? action.payload.pagedData : []
    },
    clearEngagmentList: (state: IClientCardState) => {
      state.clients = {}
      state.editEngagementCardIndex = null
      state.currentEngagement = {
        engagementID: '',
        engagementName: '',
        clientID: '',
        clientName: ''
      }
    }
  },
})

export const { openClientCard, closeClientCard, addClient, deleteClient, updateClient, setCurrentEngagement, setApplicationList, clearEngagmentList } =
  clientCardSlice.actions

export default clientCardSlice.reducer
